<template>
  <!-- Sidebar container -->
  <aside
    class="fixed inset-y-0 left-0 z-50 transition-all duration-300 ease-in-out bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-lg"
    :class="sidebarClasses"
  >
    <!-- Sidebar content -->
    <div class="flex flex-col h-full">
      <!-- Logo & Brand -->
      <div class="flex items-center h-16 px-4 border-b border-gray-200 dark:border-gray-700">
        <router-link :to="brandLink" class="flex items-center space-x-3 group">
          <img
            src="/img/logo.webp"
            alt="MailerX"
            class="h-8 group-hover:scale-110 transition-transform duration-200"
          />
        </router-link>
      </div>
      
      <!-- Navigation -->
      <nav class="flex-1 px-4 py-6 space-y-4 overflow-y-auto scrollbar-custom relative z-10">
        <!-- Context switcher (Admin/User) -->
        <div v-if="authStore.isAdmin && isOpen" class="mb-6">
          <div class="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg border border-blue-100 dark:border-blue-800">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wider">
                {{ $t('sidebar.current_context') }}
              </span>
              <button
                @click="switchContext"
                class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium transition-colors"
              >
                {{ isAdminContext ? $t('sidebar.switch_to_user') : $t('sidebar.switch_to_admin') }}
              </button>
            </div>
            <div class="flex items-center space-x-2">
              <div class="w-6 h-6 rounded-full flex items-center justify-center"
                   :class="isAdminContext ? 'bg-blue-600 dark:bg-blue-500' : 'bg-green-600 dark:bg-green-500'">
                <component :is="isAdminContext ? ShieldCheckIcon : UserIcon" class="w-3 h-3 text-white" />
              </div>
              <span class="text-sm font-medium text-gray-900 dark:text-white">
                {{ isAdminContext ? $t('sidebar.administrator') : $t('sidebar.user_dashboard') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Main navigation sections -->
        <div v-for="section in navigationSections" :key="section.id" class="space-y-1">
          <!-- Section header -->
          <div v-if="section.title && isOpen" class="px-3 py-2">
            <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ section.title }}
            </h3>
          </div>
          
          <!-- Section items -->
          <template v-for="item in section.items" :key="item.name">
            <SidebarItem
              v-if="shouldShowItem(item)"
              :to="item.to"
              :icon="item.icon"
              :label="item.label"
              :is-open="isOpen"
              :badge="getBadgeForItem(item)"
              :variant="item.variant"
            />
          </template>
        </div>
      </nav>
      
      <!-- Footer section -->
      <div class="p-4 border-t border-gray-200 dark:border-gray-700">
        <!-- Collapse toggle -->
        <button
          @click="$emit('toggle')"
          class="hidden lg:flex items-center justify-center w-full h-8 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          :class="isOpen ? '' : 'rotate-180'"
        >
          <ChevronLeftIcon class="w-5 h-5" />
        </button>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import SidebarItem from './SidebarItem.vue'

// Icons
import {
  ChevronLeftIcon,
  HomeIcon,
  MegaphoneIcon,
  UsersIcon,
  DocumentDuplicateIcon,
  BoltIcon,
  CogIcon,
  UserIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  DocumentTextIcon,
  CloudIcon
} from '@heroicons/vue/24/outline'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: true
  },
  isMobile: {
    type: Boolean,
    default: false
  }
})

defineEmits(['toggle', 'close'])

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()
const appStore = useAppStore()

// Local state
const ADMIN_PATH_RE = /\/admin(\/|$)/
const isAdminContext = ref(ADMIN_PATH_RE.test(route.path))

// Computed properties
const sidebarClasses = computed(() => ({
  'w-64': props.isOpen,
  'w-20': !props.isOpen,
  'translate-x-0': !props.isMobile || (props.isMobile && props.isOpen),
  '-translate-x-full lg:translate-x-0': props.isMobile && !props.isOpen
}))

const brandLink = computed(() => {
  const locale = route.params.locale || 'en'
  const basePath = isAdminContext.value ? '/admin/dashboard' : '/app/dashboard'
  return `/${locale}${basePath}`
})

const brandText = computed(() => {
  return isAdminContext.value ? 'MailerX Admin' : 'MailerX'
})

// Navigation configuration
const navigationSections = computed(() => {
  if (isAdminContext.value) {
    return [
      {
        id: 'admin-main',
        title: null,
        items: [
          {
            name: 'admin.dashboard',
            to: { name: 'admin.dashboard' },
            icon: ChartBarIcon,
            label: t('sidebar.admin_dashboard'),
            roles: ['admin']
          },
          {
            name: 'admin.users',
            to: { name: 'admin.users' },
            icon: UserGroupIcon,
            label: t('sidebar.user_management'),
            roles: ['admin'],
            badge: 'usersBadge'
          },
          {
            name: 'admin.plans',
            to: { name: 'admin.plans' },
            icon: CpuChipIcon,
            label: t('sidebar.subscription_plans'),
            roles: ['admin']
          },
          {
            name: 'admin.analytics',
            to: { name: 'admin.analytics' },
            icon: ChartBarIcon,
            label: t('sidebar.system_analytics'),
            roles: ['admin']
          }
        ]
      },
      {
        id: 'admin-system',
        title: t('sidebar.system'),
        items: [
          {
            name: 'admin.settings',
            to: { name: 'admin.settings' },
            icon: CogIcon,
            label: t('sidebar.system_settings'),
            roles: ['admin']
          },
          {
            name: 'admin.logs',
            to: { name: 'admin.logs' },
            icon: DocumentTextIcon,
            label: t('sidebar.system_logs'),
            roles: ['admin']
          },
          {
            name: 'admin.monitoring',
            to: { name: 'admin.monitoring' },
            icon: ExclamationTriangleIcon,
            label: t('sidebar.monitoring'),
            roles: ['admin'],
            badge: 'alertsBadge'
          }
        ]
      }
    ]
  } else {
    return [
      {
        id: 'main',
        title: null,
        items: [
          {
            name: 'dashboard',
            to: { name: 'dashboard' },
            icon: HomeIcon,
            label: t('navigation.dashboard')
          },
          {
            name: 'campaigns',
            to: { name: 'campaigns' },
            icon: MegaphoneIcon,
            label: t('navigation.campaigns'),
            badge: 'campaignsBadge'
          },
          {
            name: 'analytics',
            to: { name: 'analytics' },
            icon: ChartBarIcon,
            label: t('navigation.analytics')
          },
          {
            name: 'contacts',
            to: { name: 'contacts' },
            icon: UsersIcon,
            label: t('navigation.contacts'),
            badge: 'contactsBadge'
          },
          {
            name: 'templates',
            to: { name: 'templates' },
            icon: DocumentDuplicateIcon,
            label: t('navigation.templates')
          },
          {
            name: 'automations',
            to: { name: 'automations' },
            icon: BoltIcon,
            label: t('navigation.automations'),
            feature: 'automation_features',
            badge: 'automationsBadge'
          }
        ]
      },
      {
        id: 'account',
        title: t('sidebar.account'),
        items: [
          {
            name: 'settings',
            to: { name: 'settings' },
            icon: CogIcon,
            label: t('navigation.settings')
          },
          {
            name: 'api-docs',
            to: { name: 'api-docs' },
            icon: DocumentTextIcon,
            label: t('sidebar.api_docs'),
            feature: 'api_access'
          }
        ]
      }
    ]
  }
})

// Methods
const shouldShowItem = (item) => {
  try {
    if (!item) return false

    // Check role-based access
    if (item.roles && !item.roles.includes(authStore.user?.role)) {
      return false
    }

    // Admin always sees automations (bypass feature check)
    if (item.name === 'automations' && authStore.isAdmin) {
      return true
    }

    // Check feature-based access
    if (item.feature && !authStore.canAccessFeature?.(item.feature)) {
      return false
    }

    return true
  } catch (error) {
    console.warn('Error checking item visibility:', error)
    return false
  }
}

const getBadgeForItem = (item) => {
  try {
    if (!item?.badge) return null
    
    switch (item.badge) {
      case 'campaignsBadge':
        const drafts = appStore.dashboardStats?.draft_campaigns || 0
        return drafts > 0 ? { text: drafts, variant: 'warning' } : null
        
      case 'contactsBadge':
        const total = appStore.dashboardStats?.total_contacts || 0
        return total > 999 ? { text: '999+', variant: 'info' } : null
        
      case 'automationsBadge':
        const active = appStore.dashboardStats?.active_automations || 0
        return active > 0 ? { text: active, variant: 'success' } : null
        
      case 'usersBadge':
        const inactive = appStore.adminStats?.inactive_users || 0
        return inactive > 0 ? { text: inactive, variant: 'warning' } : null
        
      case 'alertsBadge':
        const alerts = appStore.adminStats?.system_alerts || 0
        return alerts > 0 ? { text: alerts, variant: 'danger' } : null
        
      default:
        return null
    }
  } catch (error) {
    console.warn('Error getting badge for item:', error)
    return null
  }
}

const switchContext = async () => {
  try {
    const targetPath = isAdminContext.value ? '/app/dashboard' : '/admin/dashboard'
    
    // Update context first, then navigate
    const newContext = !isAdminContext.value
    
    // Use nextTick to avoid reactivity issues during transition
    await nextTick()
    
    // Navigate to the new path
    await router.push(targetPath)
    
    // Update context after successful navigation
    isAdminContext.value = newContext
  } catch (error) {
    console.error('Error switching context:', error)
  }
}

// Watch route changes to update context
watch(() => route.path, (newPath) => {
  nextTick(() => {
    const shouldBeAdmin = ADMIN_PATH_RE.test(newPath)
    if (isAdminContext.value !== shouldBeAdmin) {
      isAdminContext.value = shouldBeAdmin
    }
  })
}, { immediate: true })
</script>

<style scoped>
/* Custom scrollbar */
.scrollbar-custom::-webkit-scrollbar {
  width: 4px;
}

.scrollbar-custom::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-custom::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.scrollbar-custom::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>